import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import {
  FiPlus, FiEye, FiSearch, FiBox, FiDollarSign, FiTag,
  FiChevronsUp, FiChevronsDown, FiZap, FiAlertTriangle,
  FiChevronLeft, FiChevronRight, FiLayers, FiClock,
  FiArrowUpRight, FiArrowDownLeft, FiEdit3, FiPackage,
  FiTrendingUp, FiShield, FiX,
} from "react-icons/fi";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { CgPrinter } from "react-icons/cg";
import { FcDocument } from "react-icons/fc";
import { BoxIcon, LockIcon } from "lucide-react";

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --cs-teal:      #0a5556;
  --cs-teal-mid:  #0c7172;
  --cs-teal-lite: #14b8a6;
  --cs-gold:      #FFD166;
  --cs-bg:        #eef6f6;
  --cs-white:     #ffffff;
  --cs-border:    rgba(10,85,86,0.10);
  --cs-text:      #0f2a2a;
  --cs-muted:     #5a8080;
  --cs-danger:    #ef4444;
  --cs-success:   #10b981;
  --cs-warning:   #f59e0b;
  --cs-info:      #3b82f6;
}

@keyframes cs__fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes cs__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes cs__spin    { to{transform:rotate(360deg)} }
@keyframes cs__modalIn { from{opacity:0;transform:scale(0.93) translateY(12px)} to{opacity:1;transform:scale(1) translateY(0)} }
@keyframes cs__pop     { 0%{transform:scale(0.85);opacity:0} 60%{transform:scale(1.07)} 100%{transform:scale(1);opacity:1} }
@keyframes cs__rowIn   { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }

/* ── page wrapper ── */
.cs__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--cs-bg);
  min-height:100vh;
  animation:cs__fadeUp 0.4s ease both;
}

/* ── header ── */
.cs__hd { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:22px; flex-wrap:wrap; }
.cs__hd-left { display:flex; align-items:center; gap:14px; }
.cs__hd-icon {
  width:52px; height:52px; border-radius:16px; flex-shrink:0;
  background:linear-gradient(135deg,var(--cs-teal),var(--cs-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#fff; box-shadow:0 6px 20px rgba(10,85,86,0.30);
}
.cs__hd-title { font-family:'Sora',sans-serif; font-size:21px; font-weight:800; color:var(--cs-text); letter-spacing:-0.03em; margin:0 0 3px; }
.cs__hd-sub   { font-size:12.5px; color:var(--cs-muted); margin:0; }

/* ── kpi strip ── */
.cs__kpis { display:flex; gap:12px; margin-bottom:22px; flex-wrap:wrap; }
.cs__kpi {
  flex:1; min-width:150px; background:var(--cs-white); border:1px solid var(--cs-border);
  border-radius:16px; padding:16px 18px; box-shadow:0 2px 10px rgba(10,85,86,0.05);
  display:flex; align-items:center; gap:13px; position:relative; overflow:hidden;
}
.cs__kpi::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--cs-teal),var(--cs-teal-lite),var(--cs-gold));
}
.cs__kpi-icon {
  width:44px; height:44px; border-radius:13px; flex-shrink:0;
  background:linear-gradient(135deg,var(--cs-teal),var(--cs-teal-lite));
  display:flex; align-items:center; justify-content:center; font-size:19px; color:#fff;
  box-shadow:0 4px 12px rgba(10,85,86,0.22);
}
.cs__kpi-icon--gold    { background:linear-gradient(135deg,#b45309,var(--cs-gold)); }
.cs__kpi-icon--danger  { background:linear-gradient(135deg,#c0392b,var(--cs-danger)); }
.cs__kpi-lbl { font-size:11px; color:var(--cs-muted); font-weight:500; margin-bottom:2px; }
.cs__kpi-val { font-family:'Sora',sans-serif; font-size:17px; font-weight:800; color:var(--cs-text); }

/* ── main card ── */
.cs__card {
  background:var(--cs-white); border-radius:20px; border:1px solid var(--cs-border);
  box-shadow:0 4px 20px rgba(10,85,86,0.07); overflow:hidden; position:relative;
}
.cs__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--cs-teal),var(--cs-teal-lite),var(--cs-gold));
}

/* ── toolbar ── */
.cs__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:12px; padding:16px 20px; border-bottom:1px solid rgba(10,85,86,0.07);
  flex-wrap:wrap; background:rgba(10,85,86,0.02);
}
.cs__toolbar-left  { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.cs__toolbar-right { display:flex; align-items:center; gap:8px; }
.cs__select {
  padding:8px 28px 8px 12px; font-size:12.5px; color:var(--cs-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; outline:none; cursor:pointer; appearance:none;
  font-family:'DM Sans',sans-serif;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center;
  transition:all 0.16s;
}
.cs__select:focus { border-color:var(--cs-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background-color:#fff; }
.cs__search {
  display:flex; align-items:center; gap:8px; background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; padding:8px 13px;
  transition:all 0.18s; width:240px;
}
.cs__search:focus-within { border-color:var(--cs-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.cs__search-icon  { color:var(--cs-muted); font-size:14px; flex-shrink:0; }
.cs__search-input {
  border:none; outline:none; background:transparent;
  font-size:13px; font-family:'DM Sans',sans-serif; color:var(--cs-text); flex:1;
}
.cs__search-input::placeholder { color:#9ababa; }
.cs__count { font-size:12px; color:var(--cs-muted); font-family:'Sora',sans-serif; font-weight:600; white-space:nowrap; }

/* per-page select */
.cs__perpage { display:flex; align-items:center; gap:6px; }
.cs__perpage-lbl { font-size:12px; color:var(--cs-muted); font-family:'Sora',sans-serif; font-weight:600; white-space:nowrap; }

/* ── table ── */
.cs__table-wrap { overflow-x:auto; }
.cs__table { width:100%; border-collapse:collapse; }
.cs__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--cs-teal);
  background:rgba(10,85,86,0.035); padding:13px 16px;
  white-space:nowrap; border-bottom:1px solid var(--cs-border);
  cursor:pointer; user-select:none; transition:color 0.14s;
}
.cs__table thead th:hover { color:var(--cs-teal-lite); }
.cs__table thead th:first-child { padding-left:22px; }
.cs__table thead th:last-child  { padding-right:22px; }

.cs__sort-icon { display:inline-flex; vertical-align:middle; margin-left:4px; opacity:.3; }
.cs__sort-icon--on { opacity:1; color:var(--cs-teal-lite); }

.cs__table tbody tr {
  border-bottom:1px solid rgba(10,85,86,0.05);
  transition:background 0.14s;
  animation:cs__rowIn 0.25s ease both;
}
.cs__table tbody tr:last-child { border-bottom:none; }
.cs__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.cs__table tbody td { padding:13px 16px; font-size:13px; color:var(--cs-text); vertical-align:middle; }
.cs__table tbody td:first-child { padding-left:22px; }
.cs__table tbody td:last-child  { padding-right:22px; }

/* id chip */
.cs__id-chip {
  display:inline-flex; align-items:center; justify-content:center;
  font-family:'Sora',sans-serif; font-size:11px; font-weight:800;
  color:var(--cs-teal); background:rgba(10,85,86,0.09);
  border:1px solid rgba(10,85,86,0.18); border-radius:8px;
  padding:3px 10px; white-space:nowrap; letter-spacing:0.03em;
}

/* product cell */
.cs__prod-cell { display:flex; align-items:center; gap:12px; }
.cs__prod-thumb {
  width:40px; height:40px; border-radius:11px; object-fit:cover; flex-shrink:0;
  border:1.5px solid var(--cs-border); background:#e8f4f4;
}
.cs__prod-name { font-size:13px; font-weight:700; color:var(--cs-text); }
.cs__prod-hsn  { font-size:10.5px; color:#9ababa; font-family:'Sora',sans-serif; }

/* type badge */
.cs__type-badge {
  display:inline-flex; align-items:center; gap:4px; padding:3px 10px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
  white-space:nowrap;
}
.cs__type-badge--product { background:rgba(10,85,86,0.09); color:var(--cs-teal); border:1px solid rgba(10,85,86,0.18); }
.cs__type-badge--service  { background:rgba(59,130,246,0.09); color:#2563eb; border:1px solid rgba(59,130,246,0.20); }

/* rate */
.cs__rate { font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800; color:var(--cs-teal); text-align:right; }

/* stock */
.cs__stock      { font-family:'Sora',sans-serif; font-size:13px; font-weight:700; text-align:center; }
.cs__stock--ok  { color:var(--cs-success); }
.cs__stock--low { color:var(--cs-danger); }
.cs__stock--na  { color:#b0cece; }

/* status badge */
.cs__status {
  display:inline-flex; align-items:center; gap:5px; padding:4px 10px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
}
.cs__status--ok      { background:rgba(16,185,129,0.10); color:#065f46; border:1px solid rgba(16,185,129,0.25); }
.cs__status--low     { background:rgba(245,158,11,0.12); color:#92400e; border:1px solid rgba(245,158,11,0.30); }
.cs__status--out     { background:rgba(239,68,68,0.10); color:#991b1b; border:1px solid rgba(239,68,68,0.25); }
.cs__status--service { background:rgba(10,85,86,0.09); color:var(--cs-teal); border:1px solid rgba(10,85,86,0.18); }

/* action buttons */
.cs__action-btn {
  display:inline-flex; align-items:center; gap:5px; padding:7px 13px;
  border-radius:9px; border:none; cursor:pointer;
  font-size:12px; font-family:'Sora',sans-serif; font-weight:700;
  transition:all 0.15s;
}
.cs__action-btn--logs {
  background:linear-gradient(135deg,var(--cs-teal),var(--cs-teal-lite));
  color:#fff; box-shadow:0 3px 10px rgba(10,85,86,0.22);
}
.cs__action-btn--logs:hover { transform:translateY(-1px); box-shadow:0 6px 16px rgba(10,85,86,0.30); }
.cs__action-btn--locked { background:rgba(10,85,86,0.05); color:#b0cece; cursor:not-allowed; border:1px solid var(--cs-border); }

/* ── skeleton ── */
.cs__skel {
  border-radius:8px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%; animation:cs__shimmer 1.4s linear infinite;
}
.cs__skel-row td { padding:14px 16px; }
.cs__skel-row td:first-child { padding-left:22px; }

/* ── empty ── */
.cs__empty { padding:60px 20px; text-align:center; }
.cs__empty-icon { width:62px; height:62px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:26px; }
.cs__empty-title { font-family:'Sora',sans-serif; font-size:16px; font-weight:700; color:var(--cs-text); margin-bottom:5px; }
.cs__empty-sub   { font-size:13px; color:var(--cs-muted); }

/* ── footer / pagination ── */
.cs__footer {
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:14px 22px; border-top:1px solid rgba(10,85,86,0.07);
  background:rgba(10,85,86,0.02); flex-wrap:wrap;
}
.cs__footer-info { font-size:12px; color:var(--cs-muted); font-family:'Sora',sans-serif; font-weight:600; }
.cs__pagination  { display:flex; align-items:center; gap:5px; }
.cs__pg-btn {
  width:32px; height:32px; border-radius:9px; border:1.5px solid var(--cs-border);
  background:var(--cs-white); cursor:pointer; display:flex; align-items:center; justify-content:center;
  color:var(--cs-muted); font-size:13px; transition:all 0.14s;
}
.cs__pg-btn:hover:not(:disabled) { border-color:var(--cs-teal); color:var(--cs-teal); background:rgba(10,85,86,0.05); }
.cs__pg-btn:disabled { opacity:.35; cursor:not-allowed; }
.cs__pg-num {
  min-width:32px; height:32px; border-radius:9px; border:1.5px solid var(--cs-border);
  background:var(--cs-white); cursor:pointer; display:flex; align-items:center; justify-content:center;
  font-size:12.5px; font-family:'Sora',sans-serif; font-weight:700; color:var(--cs-muted);
  transition:all 0.14s; padding:0 8px;
}
.cs__pg-num:hover   { border-color:var(--cs-teal); color:var(--cs-teal); }
.cs__pg-num--active { background:var(--cs-teal); color:#fff; border-color:var(--cs-teal); box-shadow:0 3px 8px rgba(10,85,86,0.25); }

/* ── MODAL ── */
.cs__overlay {
  position:fixed; inset:0; z-index:1050;
  background:rgba(10,30,30,0.60); backdrop-filter:blur(6px);
  display:flex; align-items:center; justify-content:center; padding:20px;
  animation:cs__fadeUp 0.2s ease both;
}
.cs__modal {
  background:var(--cs-white); border-radius:24px; width:100%; max-width:700px;
  box-shadow:0 30px 80px rgba(10,85,86,0.26); overflow:hidden; position:relative;
  animation:cs__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both;
}
.cs__modal::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,var(--cs-teal),var(--cs-teal-lite),var(--cs-gold)); }

.cs__modal-hd {
  display:flex; align-items:center; justify-content:space-between;
  padding:18px 24px 14px; border-bottom:1px solid var(--cs-border);
}
.cs__modal-hd-left { display:flex; align-items:center; gap:12px; }
.cs__modal-hd-icon {
  width:46px; height:46px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,var(--cs-teal),var(--cs-teal-lite));
  display:flex; align-items:center; justify-content:center; font-size:20px; color:#fff;
  box-shadow:0 4px 14px rgba(10,85,86,0.26);
}
.cs__modal-title { font-family:'Sora',sans-serif; font-weight:800; font-size:15px; color:var(--cs-text); margin:0 0 2px; }
.cs__modal-sub   { font-size:12px; color:var(--cs-muted); }
.cs__modal-close {
  width:32px; height:32px; border-radius:9px; border:none; cursor:pointer;
  background:rgba(10,85,86,0.07); color:var(--cs-muted);
  display:flex; align-items:center; justify-content:center; transition:all 0.14s;
}
.cs__modal-close:hover { background:rgba(239,68,68,0.10); color:var(--cs-danger); }

.cs__modal-body { padding:4px 0; max-height:60vh; overflow-y:auto; }

/* modal table */
.cs__log-table { width:100%; border-collapse:collapse; }
.cs__log-table thead th {
  font-family:'Sora',sans-serif; font-size:10px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--cs-teal);
  background:rgba(10,85,86,0.03); padding:11px 16px;
  border-bottom:1px solid var(--cs-border); white-space:nowrap;
}
.cs__log-table thead th:first-child { padding-left:24px; }
.cs__log-table thead th:last-child  { padding-right:24px; }
.cs__log-table tbody tr { border-bottom:1px solid rgba(10,85,86,0.05); transition:background 0.13s; }
.cs__log-table tbody tr:last-child { border-bottom:none; }
.cs__log-table tbody tr:hover { background:rgba(10,85,86,0.02); }
.cs__log-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.cs__log-table tbody td:first-child { padding-left:24px; }
.cs__log-table tbody td:last-child  { padding-right:24px; }

.cs__log-date  { font-family:'Sora',sans-serif; font-size:12.5px; font-weight:700; color:var(--cs-text); }
.cs__log-time  { font-size:10.5px; color:var(--cs-muted); }
.cs__log-reason { font-size:12.5px; color:var(--cs-muted); }

.cs__log-badge {
  display:inline-flex; align-items:center; gap:5px; padding:5px 12px;
  border-radius:50px; font-size:12px; font-family:'Sora',sans-serif; font-weight:700;
}
.cs__log-badge--add { background:rgba(16,185,129,0.12); color:#065f46; border:1px solid rgba(16,185,129,0.25); }
.cs__log-badge--sub { background:rgba(239,68,68,0.10); color:#991b1b; border:1px solid rgba(239,68,68,0.22); }

.cs__log-balance { font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800; color:var(--cs-text); }
.cs__log-prev    { font-size:10px; color:var(--cs-muted); margin-top:2px; letter-spacing:0.04em; }

.cs__modal-empty { padding:50px 20px; text-align:center; }
.cs__modal-empty-icon { width:54px; height:54px; border-radius:16px; margin:0 auto 12px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:22px; }

.cs__modal-ft { padding:14px 24px; border-top:1px solid var(--cs-border); background:rgba(10,85,86,0.02); display:flex; justify-content:flex-end; }
.cs__modal-dismiss {
  padding:9px 22px; border-radius:11px; border:1.5px solid rgba(10,85,86,0.18);
  background:rgba(10,85,86,0.07); color:var(--cs-teal);
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700; cursor:pointer; transition:all 0.15s;
}
.cs__modal-dismiss:hover { background:rgba(10,85,86,0.14); }

@media(max-width:600px){
  .cs__search{ width:100%; }
  .cs__toolbar{ flex-direction:column; align-items:stretch; }
  .cs__footer { flex-direction:column; align-items:flex-start; }
}
`;

/* ─── utils (unchanged) ─── */
const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", { style:"currency", currency:"INR", minimumFractionDigits:2, maximumFractionDigits:2 });

const getStatusInfo = (current, min, type) => {
  if (type === "Service") return { label:"Active", cls:"cs__status--service", dot:"var(--cs-teal)" };
  const stock = parseFloat(current || 0);
  const low   = parseFloat(min || 0);
  if (stock <= 0)   return { label:"Out of Stock", cls:"cs__status--out",  dot:"var(--cs-danger)" };
  if (stock <= low) return { label:"Low Stock",    cls:"cs__status--low",  dot:"var(--cs-warning)" };
  return                   { label:"In Stock",     cls:"cs__status--ok",   dot:"var(--cs-success)" };
};

/* ─── skeleton rows ─── */
const SkeletonRows = ({ count = 8 }) => Array.from({ length: count }).map((_, i) => (
  <tr className="cs__skel-row" key={i}>
    <td><div className="cs__skel" style={{ width:44, height:24, borderRadius:8 }} /></td>
    <td>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <div className="cs__skel" style={{ width:40, height:40, borderRadius:11, flexShrink:0 }} />
        <div>
          <div className="cs__skel" style={{ width:120, height:12, marginBottom:6 }} />
          <div className="cs__skel" style={{ width:80, height:9 }} />
        </div>
      </div>
    </td>
    <td><div className="cs__skel" style={{ width:70, height:22, borderRadius:50 }} /></td>
    <td><div className="cs__skel" style={{ width:80, height:12, marginLeft:"auto" }} /></td>
    <td><div className="cs__skel" style={{ width:50, height:12, margin:"0 auto" }} /></td>
    <td><div className="cs__skel" style={{ width:80, height:22, borderRadius:50 }} /></td>
    <td><div className="cs__skel" style={{ width:70, height:30, borderRadius:9 }} /></td>
  </tr>
));

/* ─── StockHistoryModal (unchanged logic, new UI) ─── */
const StockHistoryModal = ({ show, handleClose, product }) => {
  if (!show || !product) return null;
  const logs = [...(product.adjustment_logs || [])].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  return (
    <div className="cs__overlay" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="cs__modal">
        {/* header */}
        <div className="cs__modal-hd">
          <div className="cs__modal-hd-left">
            <div className="cs__modal-hd-icon"><FiClock /></div>
            <div>
              <div className="cs__modal-title">Stock Ledger</div>
              <div className="cs__modal-sub">{product.product_data?.name}</div>
            </div>
          </div>
          <button className="cs__modal-close" onClick={handleClose}><FiX size={14} /></button>
        </div>

        {/* body */}
        <div className="cs__modal-body">
          {logs.length === 0 ? (
            <div className="cs__modal-empty">
              <div className="cs__modal-empty-icon">📦</div>
              <div style={{ fontFamily:"Sora,sans-serif", fontWeight:700, fontSize:15, color:"var(--cs-text)", marginBottom:5 }}>No History Found</div>
              <div style={{ fontSize:13, color:"var(--cs-muted)" }}>No transaction history found for this item.</div>
            </div>
          ) : (
            <table className="cs__log-table">
              <thead>
                <tr>
                  <th>Activity Date</th>
                  <th>Reference / Reason</th>
                  <th style={{ textAlign:"center" }}>Adjustment</th>
                  <th style={{ textAlign:"right" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="cs__log-date">
                        {new Date(log.timestamp).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}
                      </div>
                    </td>
                    <td><span className="cs__log-reason">{log.reason}</span></td>
                    <td style={{ textAlign:"center" }}>
                      <span className={`cs__log-badge ${log.type === "add" ? "cs__log-badge--add" : "cs__log-badge--sub"}`}>
                        {log.type === "add"
                          ? <FiArrowUpRight size={12} />
                          : <FiArrowDownLeft size={12} />}
                        {log.adjustment}
                      </span>
                    </td>
                    <td style={{ textAlign:"right" }}>
                      <div className="cs__log-balance">{log.new_stock}</div>
                      <div className="cs__log-prev">PREV: {log.prev_stock}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* footer */}
        <div className="cs__modal-ft">
          <button className="cs__modal-dismiss" onClick={handleClose}>Dismiss</button>
        </div>
      </div>
    </div>
  );
};

/* ─── Pagination (unchanged logic) ─── */
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;
  const start = Math.max(1, currentPage - 2);
  const end   = Math.min(totalPages, currentPage + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="cs__pagination">
      <button className="cs__pg-btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>
        <FiChevronLeft size={13} />
      </button>
      {pages.map(p => (
        <button key={p} className={`cs__pg-num ${currentPage === p ? "cs__pg-num--active" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
      ))}
      <button className="cs__pg-btn" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}>
        <FiChevronRight size={13} />
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
function CurrentStock() {
  const [products, setProducts]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const token = localStorage.getItem("auth_token");

  /* Modal State (unchanged) */
  const [showLogModal, setShowLogModal]     = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  /* Filters (unchanged) */
  const [searchTerm, setSearchTerm]         = useState("");
  const [statusFilter, setStatusFilter]     = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("Product");
  const [sortBy, setSortBy]                 = useState({ key:"id", direction:"desc" });

  /* Pagination (unchanged) */
  const [currentPage, setCurrentPage]       = useState(1);
  const [itemsPerPage, setItemsPerPage]     = useState(10);

  /* ── API fetch (unchanged) ── */
  useEffect(() => {
    const fetchInventory = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${baseurl}getAllProductStock`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) setProducts(res.data.data);
      } catch (error) { console.error("FETCH ERROR:", error); }
      finally { setLoading(false); }
    };
    fetchInventory();
  }, [token]);

  /* ── processedProducts (unchanged) ── */
  const processedProducts = useMemo(() => {
    let list = [...products];
    if (searchTerm.trim() !== "") {
      const s = searchTerm.toLowerCase();
      list = list.filter(i =>
        String(i.id).includes(s) ||
        i.product_data?.name?.toLowerCase().includes(s) ||
        i.type?.toLowerCase().includes(s)
      );
    }
    if (categoryFilter !== "All") list = list.filter(i => i.type === categoryFilter);
    if (statusFilter !== "All") {
      list = list.filter(i => {
        const stock = parseFloat(i.current_stock || 0);
        const min   = parseFloat(i.min_stock_level || 0);
        if (statusFilter === "Low Stock") return stock <= min && i.type === "Product";
        if (statusFilter === "In Stock")  return stock > min;
        return true;
      });
    }
    list.sort((a, b) => {
      let aVal, bVal;
      if (sortBy.key === "name")      { aVal = a.product_data?.name || ""; bVal = b.product_data?.name || ""; }
      else if (sortBy.key === "unitPrice") { aVal = parseFloat(a.product_data?.rate || 0); bVal = parseFloat(b.product_data?.rate || 0); }
      else if (sortBy.key === "stock")     { aVal = parseFloat(a.current_stock || 0); bVal = parseFloat(b.current_stock || 0); }
      else { aVal = a[sortBy.key]; bVal = b[sortBy.key]; }
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [products, searchTerm, statusFilter, categoryFilter, sortBy]);

  /* ── KPIs (unchanged) ── */
  const kpis = useMemo(() => {
    const total      = products.length;
    const totalStock = products.reduce((s, p) => s + parseFloat(p.current_stock || 0), 0);
    const totalValue = products.reduce((s, p) => {
      const qty = p.type === "Service" ? 1 : parseFloat(p.current_stock || 0);
      return s + (parseFloat(p.product_data?.rate || 0) * qty);
    }, 0);
    const lowStockCount = products.filter(p =>
      p.type === "Product" && parseFloat(p.current_stock || 0) <= parseFloat(p.min_stock_level || 0)
    ).length;
    return { total, totalStock, totalValue: formatCurrency(totalValue), lowStockCount };
  }, [products]);

  /* ── handlers (unchanged) ── */
  const handleSort = (key) => {
    setSortBy(prev => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  };
  const openLogs = (prod) => { setSelectedProduct(prod); setShowLogModal(true); };

  const SortIcon = ({ columnKey }) => {
    if (sortBy.key !== columnKey) return <span className="cs__sort-icon"><FiChevronsUp size={11} /></span>;
    return sortBy.direction === "asc"
      ? <span className="cs__sort-icon cs__sort-icon--on"><FiChevronsUp size={11} /></span>
      : <span className="cs__sort-icon cs__sort-icon--on"><FiChevronsDown size={11} /></span>;
  };

  const paginatedData = processedProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="cs__wrap">

          {/* Header */}
          <div className="cs__hd">
            <div className="cs__hd-left">
              <div className="cs__hd-icon"><FiBox /></div>
              <div>
                <div className="cs__hd-title">Inventory & Services</div>
                <div className="cs__hd-sub">Live stock and service data from your business database</div>
              </div>
            </div>
          </div>

          {/* KPI Strip */}
          <div className="cs__kpis">
            {[
              { label:"Total Items",     value: kpis.total,                             icon:<FiLayers size={19} />,    cls:"" },
              { label:"Total Stock",     value: kpis.totalStock.toLocaleString(),        icon:<FiZap size={19} />,       cls:"" },
              { label:"Inventory Value", value: kpis.totalValue,                        icon:<FiDollarSign size={19} />, cls:"" },
              { label:"Low Stock Items", value: kpis.lowStockCount,                     icon:<FiAlertTriangle size={19} />, cls:"" },
            ].map((kpi, i) => (
              <div className="cs__kpi" key={i}>
                <div className={`cs__kpi-icon${kpi.cls}`}>{kpi.icon}</div>
                <div>
                  <div className="cs__kpi-lbl">{kpi.label}</div>
                  <div className="cs__kpi-val">
                    {loading ? <div className="cs__skel" style={{ width:72, height:14, marginTop:4 }} /> : kpi.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Main Card */}
          <div className="cs__card">

            {/* Toolbar */}
            <div className="cs__toolbar">
              <div className="cs__toolbar-left">
                <select className="cs__select" value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="All">All Types</option>
                  <option value="Product">Product</option>
                  <option value="Service">Service</option>
                </select>
                <select className="cs__select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="All">All Status</option>
                  <option value="In Stock">In Stock</option>
                  <option value="Low Stock">Low Stock</option>
                </select>
                <div className="cs__search">
                  <FiSearch className="cs__search-icon" />
                  <input className="cs__search-input" placeholder="Search name, ID, type…"
                    value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                  {searchTerm && (
                    <FiX size={13} style={{ color:"var(--cs-muted)", cursor:"pointer" }}
                      onClick={() => { setSearchTerm(""); setCurrentPage(1); }} />
                  )}
                </div>
              </div>
              <div className="cs__toolbar-right">
                <span className="cs__count">{processedProducts.length} items</span>
                <div className="cs__perpage">
                  <span className="cs__perpage-lbl">Show</span>
                  <select className="cs__select" style={{ width:"auto" }} value={itemsPerPage}
                    onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                    {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="cs__table-wrap">
              <table className="cs__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("id")}>ID <SortIcon columnKey="id" /></th>
                    <th onClick={() => handleSort("name")}>Item Name <SortIcon columnKey="name" /></th>
                    <th>Type</th>
                    <th style={{ textAlign:"right" }} onClick={() => handleSort("unitPrice")}>Rate <SortIcon columnKey="unitPrice" /></th>
                    <th style={{ textAlign:"center" }} onClick={() => handleSort("stock")}>Stock <SortIcon columnKey="stock" /></th>
                    <th>Status</th>
                    <th style={{ textAlign:"right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows count={itemsPerPage} />
                  ) : paginatedData.length === 0 ? (
                    <tr><td colSpan="7">
                      <div className="cs__empty">
                        <div className="cs__empty-icon">🔍</div>
                        <div className="cs__empty-title">No items found</div>
                        <div className="cs__empty-sub">Try adjusting your search or filters.</div>
                      </div>
                    </td></tr>
                  ) : paginatedData.map((prod) => {
                    const status   = getStatusInfo(prod.current_stock, prod.min_stock_level, prod.type);
                    const stockVal = parseFloat(prod.current_stock || 0);
                    const isLow    = prod.type === "Product" && stockVal <= parseFloat(prod.min_stock_level || 0);
                    return (
                      <tr key={prod.id}>
                        <td><span className="cs__id-chip">#{prod.id}</span></td>
                        <td>
                          <div className="cs__prod-cell">
                            <img className="cs__prod-thumb"
                              src={prod.product_data?.image_path ? `${imgurl}${prod.product_data.image_path}` : "https://via.placeholder.com/40"}
                              alt={prod.product_data?.name} />
                            <div>
                              <div className="cs__prod-name">{prod.product_data?.name}</div>
                              {prod.product_data?.hsn_sac && <div className="cs__prod-hsn">HSN: {prod.product_data.hsn_sac}</div>}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`cs__type-badge ${prod.type === "Product" ? "cs__type-badge--product" : "cs__type-badge--service"}`}>
                            <FiTag size={10} /> {prod.type}
                          </span>
                        </td>
                        <td><div className="cs__rate">{formatCurrency(prod.product_data?.rate)}</div></td>
                        <td>
                          {prod.type === "Service"
                            ? <div className="cs__stock cs__stock--na">N/A</div>
                            : <div className={`cs__stock ${isLow ? "cs__stock--low" : "cs__stock--ok"}`}>
                                {stockVal}
                                {prod.product_data?.unit && (
                                  <span style={{ fontSize:10.5, fontWeight:500, color:"var(--cs-muted)", marginLeft:4 }}>
                                    {prod.product_data.unit}
                                  </span>
                                )}
                              </div>
                          }
                        </td>
                        <td>
                          <span className={`cs__status ${status.cls}`}>
                            <span style={{ width:6, height:6, borderRadius:"50%", background:status.dot, flexShrink:0, display:"inline-block" }} />
                            {status.label}
                          </span>
                        </td>
                        <td style={{ textAlign:"right" }}>
                          {prod.type === "Product" && prod.product_data?.inventory_tracked !== false ? (
                            <button className="cs__action-btn cs__action-btn--logs" onClick={() => openLogs(prod)}>
                              <FiEdit3 size={12} /> Logs
                            </button>
                          ) : (
                            <span className="cs__action-btn cs__action-btn--locked">
                              <LockIcon size={11} /> Locked
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer / Pagination */}
            {!loading && (
              <div className="cs__footer">
                <div className="cs__footer-info">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, processedProducts.length)}–{Math.min(currentPage * itemsPerPage, processedProducts.length)} of {processedProducts.length} items
                </div>
                <Pagination
                  totalItems={processedProducts.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>

        </div>
      </main>

      {/* Modal */}
      <StockHistoryModal
        show={showLogModal}
        handleClose={() => setShowLogModal(false)}
        product={selectedProduct}
      />
    </>
  );
}

export default CurrentStock;
